import { type ComponentType, createContext, useContext } from "react";

/**
 * The bridge between dialogs and the app's feedback capture, defined here
 * so Dialog can consume it WITHOUT ui-kit depending on the feedback
 * package (the feedback modal is itself built from ui-kit — the dependency
 * must point one way). The feedback package provides the value; unwired
 * trees (tests, storybook) see null and dialogs behave exactly as before.
 *
 * The integration carries a COMPONENT rather than a callback because the
 * capture block must be registered for exactly as long as the dialog is
 * open, and registering needs a hook that lives in the feedback package.
 */

export interface DialogFeedbackDescriptor {
  /** Stable machine-readable key for this dialog, e.g. "item-modal". */
  name?: string;
  /** The dialog's display title, as shown to the user. */
  title?: string;
  /** The dialog's size variant. */
  size?: string;
  /**
   * The dialog's own capture block, read at submit time so it reflects the
   * latest state. Return ids, tab names, counts and booleans — never row
   * payloads: an oversized block evicts other blocks from the report.
   */
  getContext?: () => Record<string, unknown>;
}

export interface DialogFeedbackIntegration {
  /**
   * Rendered in an open dialog's title row: the report trigger, and the
   * registration of that dialog's capture block while it is mounted.
   */
  DialogFeedback: ComponentType<DialogFeedbackDescriptor>;
}

export const DialogFeedbackContext =
  createContext<DialogFeedbackIntegration | null>(null);

/** The dialog feedback integration, or null when no provider wired one up. */
export function useDialogFeedback(): DialogFeedbackIntegration | null {
  return useContext(DialogFeedbackContext);
}
